/**
 * Mouvement du personnage 3D d'un tracker en direct (ADR-017, démo) : écrêtage physique des sauts,
 * lissage One-Euro, puis suivi qui rattrape l'écart sans vitesse plafond. Affichage seulement :
 * les positions en base ne sont pas touchées. Fonctions pures, testées sans three.js.
 *
 * Repère : plan horizontal du modèle, en mètres (x, z de three.js).
 */

export interface Point2 {
  x: number;
  z: number;
}

/** Point de passage ; `y` (hauteur) est donné sur un escalier, sinon l'étage ne change pas. */
export type PathPoint = Point2 & { y?: number };

/** Réglages de démo, ajustables depuis la carte du tracker et mémorisés dans le navigateur. */
export interface TrajectorySettings {
  /** Écrêter les sauts physiquement impossibles. */
  gate: boolean;
  /** Vitesse maximale d'un piéton (m/s) : un point plus loin est ramené à la distance atteignable. */
  maxSpeedMs: number;
  /** Lissage One-Euro des positions reçues. */
  smoothing: boolean;
  /** Fréquence de coupure au repos (Hz) : plus bas = plus lisse, plus de retard à l'arrêt. */
  minCutoffHz: number;
  /** Réactivité du lissage à la vitesse : plus haut = moins de retard quand le tracker bouge. */
  beta: number;
  /** Temps pour combler l'essentiel de l'écart (s) : le personnage n'a plus de vitesse plafond. */
  catchUpS: number;
  /** Au-delà de cette distance (m), le personnage est posé directement sur la position. */
  snapM: number;
  /** Vitesse maximale sur un détour par une porte (m/s) : le contournement reste visible. */
  detourSpeedMs: number;
  /** Contourner les murs et passer par les portes (grille tirée du GLB). */
  walls: boolean;
  /** Marge gardée le long des murs (m). */
  wallClearanceM: number;
  /** Pièce sans ouverture dans le modèle : y entrer en franchissant le mur (sinon arrêt au plus près). */
  crossWalls: boolean;
  /**
   * Rejeter un point trop loin à pied (par les portes et l'escalier) pour le temps écoulé : derrière
   * un mur avec la porte loin, le point est faux.
   */
  walkGate: boolean;
  /** Imprécision de mesure tolérée en plus de la marche (m). */
  walkToleranceM: number;
  /** Points cohérents entre eux, de suite, au-delà desquels on cède : le tracker y est vraiment. */
  cedeAfter: number;
}

export const DEFAULT_TRAJECTORY: TrajectorySettings = {
  gate: true,
  maxSpeedMs: 3,
  smoothing: true,
  minCutoffHz: 0.05,
  beta: 0.3,
  catchUpS: 0.4,
  snapM: 12,
  detourSpeedMs: 8,
  walls: true,
  wallClearanceM: 0.2,
  crossWalls: false,
  walkGate: true,
  walkToleranceM: 2,
  cedeAfter: 3,
};

/** Bornes des réglages numériques (curseurs de la carte et lecture du stockage). */
export const TRAJECTORY_BOUNDS = {
  maxSpeedMs: { min: 0.5, max: 10, step: 0.5 },
  minCutoffHz: { min: 0.01, max: 2, step: 0.01 },
  beta: { min: 0, max: 2, step: 0.05 },
  catchUpS: { min: 0.1, max: 5, step: 0.1 },
  snapM: { min: 1, max: 50, step: 1 },
  wallClearanceM: { min: 0, max: 0.6, step: 0.1 },
  detourSpeedMs: { min: 1, max: 15, step: 0.5 },
  walkToleranceM: { min: 0, max: 10, step: 0.5 },
  cedeAfter: { min: 1, max: 10, step: 1 },
} as const;

type NumericKey = keyof typeof TRAJECTORY_BOUNDS;

export type TrajectoryPreset = 'raw' | 'balanced' | 'smooth';

/**
 * Préréglages de démo : « Brut » montre les positions telles que reçues (pour comparer),
 * « Équilibré » est le réglage par défaut, « Lissé » privilégie la fluidité au retard.
 */
export const TRAJECTORY_PRESETS: Record<TrajectoryPreset, TrajectorySettings> = {
  raw: { ...DEFAULT_TRAJECTORY, gate: false, smoothing: false, walls: false, walkGate: false, crossWalls: false },
  balanced: DEFAULT_TRAJECTORY,
  smooth: { ...DEFAULT_TRAJECTORY, minCutoffHz: 0.03, beta: 0.15, catchUpS: 0.8, walkToleranceM: 1.5 },
};

/** Préréglage dont `s` a toutes les valeurs, ou `null` après un réglage à la main. */
export function presetOf(s: TrajectorySettings): TrajectoryPreset | null {
  const keys = Object.keys(DEFAULT_TRAJECTORY) as (keyof TrajectorySettings)[];
  const found = (Object.keys(TRAJECTORY_PRESETS) as TrajectoryPreset[]).find(p =>
    keys.every(k => TRAJECTORY_PRESETS[p][k] === s[k])
  );
  return found ?? null;
}

/** Ramène `next` à la distance qu'un piéton peut parcourir en `dtS` depuis `prev`. */
export function gateSpeed(prev: Point2, next: Point2, dtS: number, maxSpeedMs: number): Point2 {
  const dx = next.x - prev.x;
  const dz = next.z - prev.z;
  const dist = Math.hypot(dx, dz);
  const reach = maxSpeedMs * Math.max(dtS, 0.2);
  if (dist <= reach) {
    return next;
  }
  return { x: prev.x + (dx / dist) * reach, z: prev.z + (dz / dist) * reach };
}

const alpha = (cutoffHz: number, dtS: number) => 1 / (1 + 1 / (2 * Math.PI * cutoffHz * dtS));

/** Fenêtre de l'estimation de vitesse (s) : le bruit autour d'un point fixe s'y annule, une marche non. */
const SPEED_WINDOW_S = 3;

/**
 * Vitesse nette (m/s) d'une suite de positions : écart entre la moyenne de la première moitié et
 * celle de la seconde. Un bruit qui oscille autour d'un point s'annule dans chaque moyenne.
 */
export function netSpeed(points: { t: number; p: Point2 }[]): number {
  if (points.length < 2) {
    return 0;
  }
  const half = Math.floor(points.length / 2);
  const mean = (list: { t: number; p: Point2 }[]) => ({
    t: list.reduce((a, r) => a + r.t, 0) / list.length,
    x: list.reduce((a, r) => a + r.p.x, 0) / list.length,
    z: list.reduce((a, r) => a + r.p.z, 0) / list.length,
  });
  const a = mean(points.slice(0, half));
  const b = mean(points.slice(points.length - half));
  return b.t > a.t ? Math.hypot(b.x - a.x, b.z - a.z) / (b.t - a.t) : 0;
}

/** Chaîne écrêtage + lissage d'un tracker, un appel par position reçue. */
export class TrajectoryFilter {
  #last: Point2 | null = null;
  #lastT = 0;
  /** Positions écrêtées des dernières secondes, pour la vitesse nette. */
  #recent: { t: number; p: Point2 }[] = [];

  push(raw: Point2, tS: number, s: TrajectorySettings): Point2 {
    const last = this.#last;
    if (!last) {
      this.#last = raw;
      this.#lastT = tS;
      this.#recent = [{ t: tS, p: raw }];
      return raw;
    }
    const dt = Math.max(tS - this.#lastT, 1e-3);
    const gated = s.gate ? gateSpeed(last, raw, dt, s.maxSpeedMs) : raw;
    let out = gated;
    this.#recent = [...this.#recent, { t: tS, p: gated }].filter(r => tS - r.t <= SPEED_WINDOW_S + 1e-6);
    if (s.smoothing) {
      // Vitesse nette sur la fenêtre : le bruit d'une position immobile ne passe pas pour une marche.
      const a = alpha(s.minCutoffHz + s.beta * netSpeed(this.#recent), dt);
      out = { x: last.x + a * (gated.x - last.x), z: last.z + a * (gated.z - last.z) };
    }
    this.#last = out;
    this.#lastT = tS;
    return out;
  }

  /** Changement d'étage ou saut voulu : on repart de la prochaine position. */
  reset(): void {
    this.#last = null;
    this.#recent = [];
  }
}

/**
 * Pas d'une image : le personnage comble l'écart de façon exponentielle (constante `catchUpS`,
 * indépendante de la fréquence d'images), sans vitesse plafond ; au-delà de `snapM`, il est posé.
 */
export function followStep(
  pos: Point2,
  target: Point2,
  dtS: number,
  s: TrajectorySettings
): { pos: Point2; speed: number } {
  const dx = target.x - pos.x;
  const dz = target.z - pos.z;
  const dist = Math.hypot(dx, dz);
  if (dist > s.snapM) {
    return { pos: { ...target }, speed: 0 };
  }
  if (dist < 1e-3 || dtS <= 0) {
    return { pos: { ...pos }, speed: 0 };
  }
  const k = 1 - Math.exp(-dtS / s.catchUpS);
  return { pos: { x: pos.x + dx * k, z: pos.z + dz * k }, speed: (dist * k) / dtS };
}

/**
 * Pas d'une image le long d'un chemin (points de passage, départ exclu) : même rattrapage que
 * {@link followStep}, mais sur la longueur du chemin, coin après coin, sans dépasser `detourSpeedMs`
 * (un détour par une porte reste visible). Posé au bout seulement quand la
 * cible est à plus de `snapM` à vol d'oiseau (position perdue), pas pour un long détour.
 */
export function followPath<P extends PathPoint>(
  pos: P,
  path: P[],
  dtS: number,
  s: TrajectorySettings
): { pos: P; path: P[]; speed: number } {
  if (!path.length) {
    return { pos: { ...pos }, path: [], speed: 0 };
  }
  let length = 0;
  let prev: PathPoint = pos;
  for (const p of path) {
    length += span(prev, p);
    prev = p;
  }
  const end = path[path.length - 1];
  if (Math.hypot(end.x - pos.x, end.z - pos.z) > s.snapM) {
    return { pos: { ...end }, path: [], speed: 0 };
  }
  if (length < 1e-3 || dtS <= 0) {
    return { pos: { ...pos }, path: length < 1e-3 ? [] : path, speed: 0 };
  }
  const advance = Math.min(length * (1 - Math.exp(-dtS / s.catchUpS)), s.detourSpeedMs * dtS);
  let left = advance;
  let cur: P = pos;
  const rest = [...path];
  while (rest.length) {
    const next = rest[0];
    const seg = span(cur, next);
    if (seg <= left) {
      left -= seg;
      cur = rest.shift()!;
      continue;
    }
    const k = left / seg;
    const moved = { ...cur, x: cur.x + (next.x - cur.x) * k, z: cur.z + (next.z - cur.z) * k };
    if (cur.y !== undefined && next.y !== undefined) {
      moved.y = cur.y + (next.y - cur.y) * k;
    }
    cur = moved;
    break;
  }
  return { pos: cur, path: rest, speed: advance / dtS };
}

/** Longueur d'un segment, hauteur comprise quand les deux bouts en ont une (escalier). */
function span(a: PathPoint, b: PathPoint): number {
  const dy = a.y !== undefined && b.y !== undefined ? b.y - a.y : 0;
  return Math.hypot(b.x - a.x, dy, b.z - a.z);
}

export type GateVerdict = 'accepted' | 'rejected' | 'ceded';

/**
 * Rejet des points impossibles à pied (retour d'Eddy, 2026-10-30) : un point derrière un mur, à 1 m
 * à vol d'oiseau mais à 15 m par la porte, une seconde après le précédent, est faux. La distance est
 * mesurée à pied par `distance` (par les portes et l'escalier ; `null` : inatteignable), depuis le
 * dernier point retenu.
 *
 * Si `cedeAfter` points de suite, rejetés mais cohérents entre eux, désignent un autre endroit, on
 * cède : c'est le point retenu qui était faux, ou le tracker a vraiment changé d'endroit.
 */
export class WalkGate<P> {
  #anchor: { p: P; t: number } | null = null;
  #pending: { p: P; t: number }[] = [];

  push(p: P, tS: number, s: TrajectorySettings, distance: (a: P, b: P) => number | null): GateVerdict {
    const anchor = this.#anchor;
    if (!s.walkGate || !anchor || reachable(anchor, p, tS, s, distance)) {
      this.#anchor = { p, t: tS };
      this.#pending = [];
      return 'accepted';
    }
    const last = this.#pending.at(-1);
    this.#pending = last && reachable(last, p, tS, s, distance) ? [...this.#pending, { p, t: tS }] : [{ p, t: tS }];
    if (this.#pending.length >= s.cedeAfter) {
      this.#anchor = { p, t: tS };
      this.#pending = [];
      return 'ceded';
    }
    return 'rejected';
  }

  reset(): void {
    this.#anchor = null;
    this.#pending = [];
  }
}

function reachable<P>(
  from: { p: P; t: number },
  to: P,
  tS: number,
  s: TrajectorySettings,
  distance: (a: P, b: P) => number | null
): boolean {
  const d = distance(from.p, to);
  return d !== null && d <= s.maxSpeedMs * Math.max(tS - from.t, 0.2) + s.walkToleranceM;
}

const STORAGE_KEY = 'wheere.inside.trajectory3d.v1';
const BOOLEAN_KEYS = ['gate', 'smoothing', 'walls', 'crossWalls', 'walkGate'] as const;
export type TrajectoryToggle = (typeof BOOLEAN_KEYS)[number];

/** Réglages mémorisés, bornés ; valeurs par défaut si rien, illisible ou stockage refusé. */
export function readTrajectorySettings(storage: Pick<Storage, 'getItem'>): TrajectorySettings {
  let stored: Record<string, unknown> = {};
  try {
    const raw = storage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    stored = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    // Stockage refusé ou contenu illisible : réglages par défaut (préférence locale, pas une dépendance).
  }
  const out: TrajectorySettings = { ...DEFAULT_TRAJECTORY };
  for (const key of Object.keys(TRAJECTORY_BOUNDS) as NumericKey[]) {
    const v = stored[key];
    if (typeof v === 'number' && Number.isFinite(v)) {
      out[key] = Math.min(TRAJECTORY_BOUNDS[key].max, Math.max(TRAJECTORY_BOUNDS[key].min, v));
    }
  }
  for (const key of BOOLEAN_KEYS) {
    if (typeof stored[key] === 'boolean') {
      out[key] = stored[key] as boolean;
    }
  }
  return out;
}

export function writeTrajectorySettings(storage: Pick<Storage, 'setItem'>, s: TrajectorySettings): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // Navigation privée ou quota : le réglage vaut pour la session seulement.
  }
}
